import React from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import {
    LegacyCityPropType,
    BusinessUnits,
    ProductCardBrief,
    PRODUCT_CARD_VARIANTS,
} from '@ratehub/base-ui';
import {
    CreditCardCTA,
    getCardBannerProps,
    CreditCardSynopsisShape,
    useApplyForCreditCardCallback,
} from '@ratehub/cc-common';
import { RATEHUB_BROKERAGE_IDS } from '@ratehub/mtg-common';

import { Products } from '../definitions/Products';


function FeaturedProduct({
    className,

    isSponsored,
    hasPromo,

    cardTitle,
    applyText,
    applyHref,
    applyHrefRedirect,
    imageSrc,
    imageAlt,
    rate,
    description,
    productTypeOverride,

    productType,
    providerId,
    providerName,

    creditCardSynopsis,
    analyticsObject,

    mortgageCallback,

    hasMinWidth = true,
    hasMaxWidth = true,
    isCompact = false,
    variant = PRODUCT_CARD_VARIANTS.ONE_TITLE,

    as = 'li',

    city,
    hideCTAIfNotMonetized = false,
    ...otherProps
}) {
    const intl = useIntl();

    const applyForCreditCardCallback = useApplyForCreditCardCallback();

    const isARatehubCompany = productType === Products.MORTGAGES
        && Object.values(RATEHUB_BROKERAGE_IDS).includes(providerId);

    /* Experimental prop - currently a CC-only experiment, but if it
    wins, this will need to come from someplace else for the other BUs
    I would suggest setting a productTitle as part of each product
    during enrichment but this is a quick way to do this for CC
    without having to re-work that whole system. Discussing with
    product where this would come from in other BUs now. */
    const productName = creditCardSynopsis?.name;

    async function handleCCCallback() {
        applyForCreditCardCallback({
            cardInfo: creditCardSynopsis,
            applyURLOverride: applyHrefRedirect ?? applyHref,
        });
    }

    async function handleMortgageClick() {
        mortgageCallback?.({ rate, city });
    }

    function getComponentWithProps() {
        const componentClassName = hasPromo
            ? 'has-promo'
            : '';

        switch (productType) {
            case Products.CREDIT_CARDS: {
                const {
                    isMonetized,
                    cardTypes,
                    giftCardOffer,
                    hasReferralOffer,
                } = creditCardSynopsis;

                return hideCTAIfNotMonetized && !isMonetized
                    ? [
                        'div',
                        {},
                    ]
                    : [
                        CreditCardCTA,
                        {
                            cardTypes,
                            giftCardOffer,
                            hasReferralOffer,
                            cardBannerProps: getCardBannerProps({ giftCardOffer, hasReferralOffer }, intl),
                            cardId: creditCardSynopsis.id,

                            onClick: handleCCCallback,
                            // short-term? hack to squeeze in four featured products, including a CTA "check eligibility"
                            // buttonSize: 'large',     // 1.125 rem (original value, but too wide with 4-up and long CTA)
                            // buttonSize: 'medium',    // 1 rem
                            buttonSize: 'small',        // 1 rem, but less padding
                            className: componentClassName,
                            productType: productTypeOverride,
                            applyText: applyText?.trim().length > 0
                                ? applyText
                                : undefined,
                        },
                    ];
            }

            case Products.MORTGAGES:
                return [
                    'div',
                    {
                        onClick: handleMortgageClick,
                        className: componentClassName,
                    },
                ];

            default:
                return [
                    'a',
                    {
                        href: applyHrefRedirect || applyHref,
                        target: '_blank',
                        rel: 'noopener',
                        className: componentClassName,
                    },
                ];
        }
    }

    return (
        <ProductCardBrief
            {...otherProps}
            analyticsObject={analyticsObject}
            className={className}
            CTAComponentWithProps={getComponentWithProps()}
            isSponsored={isSponsored}
            rate={rate}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
            isARatehubCompany={isARatehubCompany}
            description={description}
            productType={productType}
            applyText={applyText}
            cardTitle={cardTitle}
            productName={productName}
            providerName={providerName}
            hasPromo={hasPromo}
            hasMinWidth={hasMinWidth}
            hasMaxWidth={hasMaxWidth}
            isCompact={isCompact}
            variant={variant}
            as={as}
        />
    );
}

FeaturedProduct.propTypes = {
    className: PropTypes.string,

    isSponsored: PropTypes.bool.isRequired,
    hasPromo: PropTypes.bool.isRequired,

    cardTitle: PropTypes.string.isRequired,
    productName: PropTypes.string,

    applyHref: PropTypes.string.isRequired,
    applyText: PropTypes.string,
    imageSrc: PropTypes.string.isRequired,
    imageAlt: PropTypes.string.isRequired,

    applyHrefRedirect: PropTypes.string,
    rate: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.object,
    ]),
    description: PropTypes.string,
    productTypeOverride: PropTypes.string,

    productType: PropTypes.oneOf(
        [ ...new Set(Object.values(Products).concat(Object.values(BusinessUnits))) ],
    ).isRequired,

    providerId: PropTypes.number,
    providerName: PropTypes.string,

    creditCardSynopsis: PropTypes.shape(CreditCardSynopsisShape),
    analyticsObject: PropTypes.object.isRequired,

    mortgageCallback: PropTypes.func,

    hasMinWidth: PropTypes.bool,
    hasMaxWidth: PropTypes.bool,
    isCompact: PropTypes.bool,
    variant: PropTypes.oneOf(Object.values(PRODUCT_CARD_VARIANTS)),

    as: PropTypes.string,
    city: LegacyCityPropType.isRequired,
    hideCTAIfNotMonetized: PropTypes.bool,      // this prop is only relevant to credit cards at the moment
};

export default FeaturedProduct;
